import { ReactNode, useCallback, useMemo, useState } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT } from "@common/schemas/domains/accounting/scheduleMilestone.schema";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import {
    MilestoneAchievedBulkTarget,
    MilestoneAchievedModal,
} from "./MilestoneAchievedModal";
import { MilestoneAchievedBulkRow } from "./MilestoneAchievedRowsList";
import {
    MILESTONE_ACHIEVED_BULK_DISABLED_REASON,
    MILESTONE_ACHIEVED_BULK_LIMIT_REASON,
} from "./milestoneAchievedCopy";
import { getSelectedMilestoneRows } from "./milestoneSelectionHelpers";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";

export function getMilestoneAchievedDisabledReason(
    rows: MilestoneAchievedBulkRow[],
): string | undefined {
    if (rows.length === 0) {
        return MILESTONE_ACHIEVED_BULK_DISABLED_REASON;
    }
    return rows.length > SCHEDULE_MILESTONE_RECOGNIZE_BULK_LIMIT
        ? MILESTONE_ACHIEVED_BULK_LIMIT_REASON
        : undefined;
}

/** Bulk "Milestone achieved" for the schedules list, plus its modal. */
export function useScheduleApplicationsBulkMilestones(options: {
    rows: ScheduleApplicationParentRow[];
    rowSelection: RowSelectionState;
    isEnabled: boolean;
    onCompleted: () => void;
}): {
    selectedMilestoneRowCount: number;
    onMilestoneAchieved: (() => void) | undefined;
    milestoneAchievedDisabledReason: string | undefined;
    modal: ReactNode;
} {
    const { rows, rowSelection, isEnabled, onCompleted } = options;
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const [target, setTarget] = useState<MilestoneAchievedBulkTarget | null>(
        null,
    );

    const selection = useMemo(() => {
        const { milestoneRows, skippedCount } = getSelectedMilestoneRows({
            rows,
            rowSelection,
        });
        return {
            skippedCount,
            bulkRows: milestoneRows.flatMap(
                ({ parent, row }): MilestoneAchievedBulkRow[] =>
                    row.milestone
                        ? [
                              {
                                  applicationId: row.scheduleApplicationId,
                                  entityId: row.entityId,
                                  scheduleName: parent.description,
                                  amount: row.amount,
                                  milestone: row.milestone,
                              },
                          ]
                        : [],
            ),
        };
    }, [rows, rowSelection]);

    const onMilestoneAchieved = useCallback(() => {
        const [first] = selection.bulkRows;
        if (!first || refuseIfViewOnly()) {
            return;
        }
        setTarget({
            rows: selection.bulkRows,
            skippedCount: selection.skippedCount,
        });
    }, [refuseIfViewOnly, selection]);

    if (!isEnabled) {
        return {
            selectedMilestoneRowCount: 0,
            onMilestoneAchieved: undefined,
            milestoneAchievedDisabledReason: undefined,
            modal: null,
        };
    }
    return {
        selectedMilestoneRowCount: selection.bulkRows.length,
        onMilestoneAchieved,
        milestoneAchievedDisabledReason: getMilestoneAchievedDisabledReason(
            selection.bulkRows,
        ),
        modal: (
            <MilestoneAchievedModal
                target={target}
                onClose={() => setTarget(null)}
                onCompleted={onCompleted}
            />
        ),
    };
}
